import { Router } from '@angular/router';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Store } from '@ngrx/store';
import { setClientStatus } from 'app/core/store';
import * as fromRoot from 'app/core/store';
import { MemberClient } from 'app/shared/authentication/models/member-client.model';
import { Nonce } from 'app/shared/authentication/models/nonce.model';
import { SsoLogin } from 'app/shared/authentication/models/sso-login.model';
import {
  AuthResult,
  UserLoginDto,
  UserVerification,
} from 'app/shared/authentication/models/user-login.model';
import { CmUser } from 'app/shared/authentication/models/user.model';
import { getAccessToken } from 'app/shared/authentication/utils/auth.util';
import { HttpHelperService } from 'app/shared/forms/services/http-helper.service';
import { Identifier, LocalStorageService } from 'app/shared/storage/services/local-storage.service';
import DeviceDetector from 'device-detector-js';
import { environment } from 'environments/environment';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LoginProviderUserIdentifier } from '../models/login-provider-user-identifier';
import { SysLoginProvider } from '../models/sys-login-provider.model';
import { ClinicOnlineBookingSetup } from 'app/shared/portal/models/clinic-online-booking-setup.model';

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  baseUrl = environment.apiUrl;
  userAuthenticationChanged$: Observable<CmUser | undefined>;

  private jwtHelper = new JwtHelperService();
  private destroyed = new Subject<void>();

  private userAuthenticationChangedSub = new BehaviorSubject<CmUser | undefined>(undefined);
  subscription: Subscription = new Subscription();
  constructor(
    private http: HttpClient,
    private localStorageService: LocalStorageService,
    private httpHelperService: HttpHelperService,
    private router: Router,
    private store: Store<fromRoot.State>
  ) {
    this.userAuthenticationChanged$ = this.userAuthenticationChangedSub.asObservable();
    this.localStorageService
      .getStorageItem(Identifier.TYPE_USER)
      .pipe(takeUntil(this.destroyed))
      .subscribe(jsonUser => {
        const token = getAccessToken();
        if (jsonUser && token && !this.jwtHelper.isTokenExpired(token)) {
          const user = JSON.parse(jsonUser);
          this.userAuthenticationChangedSub.next(user);
        } else {
          this.userAuthenticationChangedSub.next(undefined);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroyed.next();
  }

  usernameVerification(userName: string): Observable<UserVerification> {
    const url = `${this.baseUrl}users/clients/validate`;
    return this.http.post<UserVerification>(url, { userName: userName });
  }

  reclaimClientAccount(form: UntypedFormGroup): Observable<AuthResult> {
    const url = `${this.baseUrl}users/clients/reclaimaccount`;
    return this.http.put<AuthResult>(url, this.httpHelperService.createInsertBody(form));
  }

  loginUser(authData: UserLoginDto): Observable<AuthResult> {
    const device = this.includeAuditInformation();
    authData.userAgent = `${device?.client?.name || ''} ${device?.client?.version || ''}`;
    authData.platform = `${device?.os?.name || ''} ${device?.os?.version || ''} ${
      device?.os?.platform || ''
    }`;

    return this.http.post<AuthResult>(
      `${this.baseUrl}token/referrers/login`,
      JSON.stringify(authData)
    );
  }

  loginUserWithToken(token: string): Observable<AuthResult> {
    return this.http.post<AuthResult>(
      `${this.baseUrl}token/referrers/login/permanent`,
      {},
      { params: { token } }
    );
  }

  processToken(token: string, isMemberLogin = false): CmUser {
    if (!isMemberLogin) {
      this.localStorageService.setStorageItem(Identifier.TYPE_MEMBER_TOKEN, token);
    }
    this.localStorageService.setStorageItem(Identifier.TYPE_TOKEN, token);
    const decodedToken = this.jwtHelper.decodeToken(token);
    const cmUser: CmUser = {
      userId: decodedToken.nameid,
      email: decodedToken.email,
      role: decodedToken.role,
      permissions: decodedToken.permissions,
      fullName: decodedToken.fullName,
      entityId: +decodedToken.entityId,
      sysEntityTypeId: +decodedToken.sysEntityTypeId,
      photoFileUri: decodedToken.photoFileUri,
      correspondenceLanguageId: +decodedToken.correspondenceLanguageId,
      clinics: decodedToken.clinicId,
    };
    this.localStorageService.setStorageItem(Identifier.TYPE_USER, JSON.stringify(cmUser));
    return cmUser;
  }

  logout(): void {
    const needsSSCLeared =
      localStorage.getItem(Identifier.TYPE_SSO_INFO) && localStorage.getItem(Identifier.TYPE_TOKEN);
    this.localStorageService.removeStorageItem(Identifier.TYPE_TOKEN);
    this.localStorageService.removeStorageItem(Identifier.TYPE_MEMBER_TOKEN);
    this.localStorageService.removeStorageItem(Identifier.TYPE_USER);
    this.localStorageService.removeStorageItem(Identifier.TYPE_ChatToken);
    this.store.dispatch(setClientStatus({ clientStatus: undefined }));
    this.store.dispatch(fromRoot.setTermsId({ termsId: 0 }));
    if (needsSSCLeared) {
      window.sessionStorage.clear();
    }
  }

  loginType(value: string): Observable<number[]> {
    const url = `${this.baseUrl}token/referrers/logintypes`;
    return this.http.post<number[]>(url, JSON.stringify(value));
  }

  beginRegistration(registrationForm: UntypedFormGroup): Observable<HttpResponse<void>> {
    const url = `${this.baseUrl}users/beginregistration`;
    return this.http.post<void>(url, registrationForm.value, {
      observe: 'response',
    });
  }

  completeRegistration(completeRegistrationForm: UntypedFormGroup): Observable<HttpResponse<void>> {
    const url = `${this.baseUrl}users/completeregistration`;
    return this.http.post<void>(url, completeRegistrationForm.value, {
      observe: 'response',
    });
  }

  getNonce(nonceId: number): Observable<Nonce> {
    const url = `${this.baseUrl}nonces/${nonceId}`;
    return this.http.get<Nonce>(url);
  }

  loginWithNonce(nonceId: string): Observable<AuthResult> {
    const url = `${this.baseUrl}token/nonces/${nonceId}`;
    return this.http.post<AuthResult>(url, {});
  }

  getNonceWithVerificationCode(
    verificationCode: string,
    additionalInformation: string
  ): Observable<Nonce> {
    const url = `${this.baseUrl}nonces/noncewithverificationcode?verificationCode=${verificationCode}&&additionalInformation=${additionalInformation}`;
    return this.http.get<Nonce>(url);
  }

  forgotPassword(forgottenPassword: string): Observable<HttpResponse<void>> {
    return this.http.put<void>(`${this.baseUrl}users/forgotpassword`, forgottenPassword, {
      observe: 'response',
    });
  }

  resetPassword(resetPasswordForm: UntypedFormGroup): Observable<HttpResponse<void>> {
    return this.http.put<void>(`${this.baseUrl}users/resetpassword`, resetPasswordForm.value, {
      observe: 'response',
    });
  }

  changePassword(
    userId: number,
    resetPasswordForm: UntypedFormGroup
  ): Observable<HttpResponse<void>> {
    return this.http.put<void>(
      `${this.baseUrl}users/${userId}/changepassword`,
      resetPasswordForm.value,
      { observe: 'response' }
    );
  }

  changeUsername(userId: number, resetUserNameFormGroup: UntypedFormGroup): Observable<AuthResult> {
    const url = `${this.baseUrl}users/${userId}/changeusername`;
    return this.http.put<AuthResult>(url, resetUserNameFormGroup.value);
  }

  changeEmail(email: string): Observable<HttpResponse<void>> {
    const url = `${this.baseUrl}nonces/changeEmailAddress`;
    return this.http.post<void>(url, JSON.stringify(email), { observe: 'response' });
  }

  updateConfirmedEmail(nonceId: string): Observable<any> {
    const url = `${this.baseUrl}nonces/updateconfirmemail/${nonceId}`;
    return this.http.put<any>(url, {});
  }

  getLoginAccessCode(form: UntypedFormGroup): Observable<HttpResponse<void>> {
    const url = `${this.baseUrl}users/login/onetimeaccess`;
    return this.http.post<void>(url, this.httpHelperService.createInsertBody(form), {
      observe: 'response',
    });
  }

  useOneTimeCode(accessCode: string): Observable<{ token: string }> {
    const path = 'token/onetimecode';
    return this.http.post<{ token: string }>(`${this.baseUrl}${path}`, JSON.stringify(accessCode));
  }

  registerNewUser(userCreationForm: UntypedFormGroup): Observable<HttpResponse<void>> {
    const url = `${this.baseUrl}users/register`;
    return this.http.post<void>(url, this.httpHelperService.createInsertBody(userCreationForm), {
      observe: 'response',
    });
  }

  updateProfile(
    clientFormGroup: UntypedFormGroup,
    clientId: number
  ): Observable<HttpResponse<void>> {
    const url = `${this.baseUrl}clients/${clientId}/profile`;
    return this.http.patch<void>(url, this.httpHelperService.createPatchBody(clientFormGroup), {
      observe: 'response',
    });
  }

  createClient(clientFormGroup: UntypedFormGroup): Observable<HttpResponse<any>> {
    const url = `${this.baseUrl}clients`;
    return this.http.post<any>(url, this.httpHelperService.createInsertBody(clientFormGroup));
  }

  getMembers(): Observable<MemberClient[]> {
    const url = `${this.baseUrl}clientgrouprelatedmembers/authorized`;
    const headers = new HttpHeaders({ 'Member-Interceptor': '' });
    return this.http.get<MemberClient[]>(url, { headers });
  }

  loginWithMember(memberId: number): Observable<AuthResult> {
    const url = `${this.baseUrl}token/login/members/${memberId}`;
    const headers = new HttpHeaders({ 'Member-Interceptor': '' });
    return this.http.post<AuthResult>(url, {}, { headers });
  }

  getSysLoginProviders(): Observable<SysLoginProvider[]> {
    const url = `${this.baseUrl}security/systems/loginproviders`;
    return this.http.get<SysLoginProvider[]>(url);
  }

  getLinkedLoginProviderUserIdentifiers(): Observable<LoginProviderUserIdentifier[]> {
    const url = `${this.baseUrl}login/provider`;
    return this.http.get<LoginProviderUserIdentifier[]>(url);
  }

  getTermsAndConditions(url: string): Observable<Blob> {
    return this.http.get(url, { responseType: 'blob' });
  }

  createTermsAndConditionsAccept(termsId: number): Observable<HttpResponse<any>> {
    const device = this.includeAuditInformation();
    const termsAndConditionsAcceptationCreation: any = {
      browser: `${device?.client?.name || ''} ${device?.client?.version || ''}`,
      operatingSystem: `${device?.os?.name || ''} ${device?.os?.version || ''} ${
        device?.os?.platform || ''
      }`,
    };

    const url = `${this.baseUrl}termsandconditionsacceptationaudit/${termsId}`;
    return this.http.post<any>(url, termsAndConditionsAcceptationCreation);
  }

  linkAccountWithLoginProvider(
    sysLoginProviderId: number,
    redirectUrl: string,
    token: string,
    verifierToken: string
  ): Observable<void> {
    const url = `${this.baseUrl}login/provider/link`;
    return this.http.post<void>(url, {
      sysLoginProviderId: sysLoginProviderId,
      redirectUrl: redirectUrl,
      token: token,
      verifierToken: verifierToken,
    });
  }

  getTwitterOauthToken(callBackUrl: string): Observable<string | null> {
    const url = `${this.baseUrl}login/provider/twitter/requesttoken`;
    return this.http.post<string>(
      url,
      { callBackUrl: callBackUrl },
      {
        responseType: 'text' as 'json',
      }
    );
  }

  loginWithLoginProvider(
    sysLoginProviderId: number,
    redirectUrl: string,
    token: string,
    verifierToken: string
  ): Observable<AuthResult> {
    const url = `${this.baseUrl}login/provider`;
    return this.http.post<AuthResult>(url, {
      sysLoginProviderId: sysLoginProviderId,
      redirectUrl: redirectUrl,
      token: token,
      verifierToken: verifierToken,
    });
  }

  deleteLoginProviderLink(sysLoginProviderId: number): Observable<any> {
    const url = `${this.baseUrl}login/provider/${sysLoginProviderId}`;
    return this.http.delete<any>(url, { observe: 'response' });
  }

  createBookingDetailsNonce(otherData: any): Observable<string> {
    const url = `${this.baseUrl}nonces/bookingdetails`;
    return this.http.post<string>(url, otherData, {
      responseType: 'text' as 'json',
    });
  }

  getSsoProviderToken(ssoLogin: SsoLogin): Observable<AuthResult> {
    const url = `${this.baseUrl}token/loginmyhealthportal`;
    return this.http.post<AuthResult>(url, ssoLogin);
  }

  getMemberEntityId(): number | undefined {
    const token = localStorage.getItem('memberToken');
    if (token) {
      const decodedToken = this.jwtHelper.decodeToken(token);
      return +decodedToken.entityId;
    } else {
      return undefined;
    }
  }

  getMemberFullName(): string {
    const token = localStorage.getItem('memberToken');
    if (token) {
      const decodedToken = this.jwtHelper.decodeToken(token);
      return decodedToken.fullName;
    } else {
      return '';
    }
  }

  private includeAuditInformation(): any {
    const deviceDetector = new DeviceDetector();
    const navigatorUserAgent = window.navigator.userAgent.toLowerCase();
    const device = deviceDetector.parse(navigatorUserAgent);

    return device;
  }

  canBrowse(): void {
    const token = getAccessToken();
    const portalSetupJson = localStorage.getItem(Identifier.TYPE_PORTAL_SETUP);
    const portalSetup = portalSetupJson
      ? (JSON.parse(portalSetupJson) as ClinicOnlineBookingSetup)
      : undefined;
    if (
      (!token || this.jwtHelper.isTokenExpired(token)) &&
      portalSetup &&
      portalSetup.requireAuthenticationToSeeAvailabilities
    ) {
      this.router.navigate([decodeURIComponent('/landing')]).then();
    }
  }
}
